import {
  ChangeDetectionStrategy,
  Component,
  effect,
  Inject,
  OnDestroy,
  PLATFORM_ID,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { MatMenuTrigger } from '@angular/material/menu';
import { DecimalPipe, DOCUMENT, isPlatformBrowser, NgIf } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

import { CartModel } from '../../cart.model';
import { ColorSizePipe, ProductLinkPipe } from './pipes';
import { Cart_Item } from '@proto/store/api/store/cart';
import { ImageLoadingComponent } from '@common/image-loading/image-loading.component';
import { RouteHelper } from '@helper/route-paths';
import { MarketingCategoryModel } from '@common/marketing-category/marketing-category.model';
import { MenuModule } from '@module/menu-module';
import { FocusRemoverDirective } from '@helper/focus-remover.directive';
import { ProductDetailComponent } from '../../../outfit/desktop/product-detail/product-detail.component';

import { tap } from 'rxjs';

@Component({
  selector: 'app-menu-cart',
  templateUrl: './menu-cart.component.html',
  styleUrls: ['./menu-cart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MenuModule,
    DecimalPipe,
    NgIf,
    ImageLoadingComponent,
    RouterLink,
    FocusRemoverDirective,
    ColorSizePipe,
    ProductLinkPipe,
    MatDialogModule,
  ],
})
export class MenuCartComponent implements OnDestroy {
  readonly triggerDelay = 100;
  readonly notificationDelay = 2000;
  readonly isBrowser = isPlatformBrowser(this.platformId);
  readonly notificationItem = signal<Cart_Item | undefined>(undefined);
  readonly mouseX = signal(0);
  readonly mouseY = signal(0);
  readonly rootElement = ':root';
  readonly outfitCartMarginTopKey = '--outfit-cart-margin-top';
  readonly outfitCartMarginTopValue = 'calc(50vh - 412px)';
  readonly outfitCartMarginRightKey = '--outfit-cart-margin-right';
  readonly outfitCartMarginRightValue = '-90px';

  @ViewChild(MatMenuTrigger) trigger!: MatMenuTrigger;

  private intervalId: ReturnType<typeof setTimeout> | undefined;
  private notificationIntervalId: ReturnType<typeof setTimeout> | undefined;

  constructor(
    public viewModel: CartModel,
    @Inject(PLATFORM_ID) private platformId: object,
    @Inject(DOCUMENT) private document: Document,
    dialog: MatDialog,
    private categoryViewModel: MarketingCategoryModel,
    private router: Router
  ) {
    this.viewModel.cartAdded
      .pipe(
        tap(item => {
          this.trigger.openMenu();
          const hasProductDetail = dialog.openDialogs.some(d => d.componentInstance instanceof ProductDetailComponent);
          const r: HTMLElement | null = document.querySelector(this.rootElement);
          r?.style.setProperty(this.outfitCartMarginTopKey, hasProductDetail ? this.outfitCartMarginTopValue : '0');
          r?.style.setProperty(this.outfitCartMarginRightKey, hasProductDetail ? this.outfitCartMarginRightValue : '0');
          this.notificationItem.set(item);
        }),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        clearTimeout(this.notificationIntervalId);
        this.notificationIntervalId = setTimeout(() => this.trigger.closeMenu(), this.notificationDelay);
      });
    effect(() => {
      if (!this.viewModel.totalQtyDisplay()) {
        // 當刪除到沒有商品的時候, 關閉 menu.
        setTimeout(() => this.trigger?.closeMenu(), this.triggerDelay);
      }
    });
  }

  ngOnDestroy(): void {
    this.clearTimeout();
  }

  openMenu(event: MouseEvent, trigger: MatMenuTrigger): void {
    this.mouseX.set(event.clientX);
    this.mouseY.set(event.clientY);
    this.clearTimeout();
    if (!this.viewModel.totalQtyDisplay()) {
      return;
    }
    const r: HTMLElement | null = this.document.querySelector(this.rootElement);
    r?.style.setProperty(this.outfitCartMarginTopKey, '0');
    r?.style.setProperty(this.outfitCartMarginRightKey, '0');
    this.notificationItem.set(undefined);
    this.intervalId = setTimeout(() => {
      if (trigger.menuOpen) {
        return;
      }
      trigger.openMenu();
    }, this.triggerDelay);
  }

  closeMenu(event: MouseEvent, trigger: MatMenuTrigger): void {
    if (this.mouseX() === event.clientX && this.mouseY() === event.clientY) {
      // 避免造成 不斷的開關 MatMenu,
      // 解法：打開 MatMenu 的滑鼠位置, 跟關閉的滑鼠位置, 不能相同.
      // 原因是 mouseOver 打開後, 原本滑鼠位置被打開的 MatMenu 蓋到後, 自然觸發 mouseLeave.
      // 而觸發 mouseLeave 後, 關閉 MatMenu, 滑鼠又會觸發到後面的按鈕 mouseOver 又開啟.
      return;
    }
    this.clearTimeout();
    this.intervalId = setTimeout(() => trigger.closeMenu(), this.triggerDelay);
  }

  clearTimeout(): void {
    clearTimeout(this.intervalId);
    clearTimeout(this.notificationIntervalId);
  }

  goCart(trigger?: MatMenuTrigger): void {
    trigger?.closeMenu();
    if (this.router.url === `/${RouteHelper.cart()}`) {
      this.viewModel.getCart();
      return;
    }
    this.categoryViewModel.resetCategories();
    this.router.navigate([RouteHelper.cart()]);
  }
}
